import React, { forwardRef, useEffect, useState } from 'react';
import { cn } from '../lib/cn';
import { Wifi, WifiOff } from 'lucide-react';

export interface OfflineSyncBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  pendingCount?: number;
}

export const OfflineSyncBadge = forwardRef<HTMLDivElement, OfflineSyncBadgeProps>(
  ({ pendingCount = 0, className, ...props }, ref) => {
    const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

    useEffect(() => {
      const handleOnline = () => setIsOnline(true);
      const handleOffline = () => setIsOnline(false);

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }, []);

    return (
      <div 
        ref={ref}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors duration-300",
          isOnline ? "bg-success/10 text-success" : "bg-amber-500/10 text-amber-600 dark:text-amber-500",
          className
        )}
        role="status"
        aria-live="polite"
        {...props}
      >
        {isOnline ? (
          <>
            <Wifi className="w-3.5 h-3.5" />
            <span>Online &middot; All synced</span>
          </>
        ) : (
          <>
            <WifiOff className="w-3.5 h-3.5" />
            <span>Offline &middot; {pendingCount} {pendingCount === 1 ? 'item' : 'items'} queued</span>
          </>
        )}
      </div>
    );
  }
);

OfflineSyncBadge.displayName = 'OfflineSyncBadge';
